/**
 * Region-aware display names for the subjects a teacher can pick on their
 * profile.
 *
 * Texas doesn't call the subject "ELA" — its standards (TEKS) and the STAAR test
 * both say "Reading Language Arts", and teachers there say "RLA". The stored
 * value stays `'ELA'` in every region: it is matched against lesson metadata,
 * generator presets and the home-page filters, so renaming it per region would
 * fork the data. Only the label changes.
 *
 * Long form is for a field where there's room (the profile's subject chips);
 * short form is for tight spots like the sidebar's one-line subtitle.
 */
const ELA = 'ELA';

const TEXAS_REGION = 'TX';

// Every spelling that has meant this one subject. Onboarding lets teachers type
// their own subjects, and imported rosters have carried the Texas names, so a
// profile can hold 'RLA' or 'Reading Language Arts' where the chip list only
// knows 'ELA'. The profile matches chips by exact string, so an alias used to
// slip past the known-options list and render a *second* chip for the same
// subject after "Other" — a teacher saw both RLA and ELA offered.
const ELA_ALIASES = [
  'ela',
  'rla',
  'english language arts',
  'reading language arts',
  'reading/language arts',
  'reading and language arts',
];

export const isTexasRegion = (region?: string): boolean =>
  region?.toUpperCase() === TEXAS_REGION;

/**
 * The value to store for a subject the user picked or had stored already.
 *
 * Folds the aliases above onto `'ELA'` and trims, so one subject is one value no
 * matter which spelling it arrived as. Anything unrecognised is passed through
 * trimmed — onboarding's free-text subjects are legitimate and must survive.
 */
export function canonicalSubject(subject: string): string {
  const trimmed = subject.trim();
  return ELA_ALIASES.includes(trimmed.toLowerCase()) ? ELA : trimmed;
}

/**
 * Canonicalise a stored list and drop duplicates, keeping first-seen order.
 *
 * Case-insensitive on the comparison as well as alias-aware: 'Math' and 'math'
 * are the same chip, and rendering both is the same bug in a smaller costume.
 */
export function canonicalSubjects(subjects: string[]): string[] {
  const seen = new Set<string>();
  return subjects.reduce<string[]>((acc, subject) => {
    const canonical = canonicalSubject(subject);
    const key = canonical.toLowerCase();
    if (!canonical || seen.has(key)) return acc;
    seen.add(key);
    return [...acc, canonical];
  }, []);
}

export function subjectDisplayName(subject: string, region?: string): string {
  if (canonicalSubject(subject) === ELA && isTexasRegion(region)) {
    return 'Reading Language Arts';
  }
  return subject;
}

export function subjectShortName(subject: string, region?: string): string {
  if (canonicalSubject(subject) === ELA && isTexasRegion(region)) return 'RLA';
  return subject;
}
